import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { HubCard } from "@/components/media/HubCard";
import { LiveViewer } from "@/components/media/LiveViewer";
import { LiveSchedule } from "@/components/media/LiveSchedule";
import { LiveAlertCTA } from "@/components/media/LiveAlertCTA";
import { ArticlesGrid } from "@/components/media/ArticlesGrid";
import { useAuth } from "@/context/AuthContext";

const MyBroadcasts = () => {
  const { data = [] } = useQuery({ queryKey: ["my-broadcasts"], queryFn: () => api.get("/me/broadcasts").then((r) => r.data) });
  if (!data.length) return null;
  return (
    <section className="mb-14" data-testid="media-section-my-broadcasts">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1A242B] mb-5">Your broadcasts</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((b) => (
          <Link key={b.id} to={`/events/${b.id}/watch`} className="eyw-card p-5 hover:-translate-y-0.5 transition-transform" data-testid={`my-broadcast-${b.id}`}>
            <p className="text-[#1A242B]">{b.title}</p>
            <p className="text-xs text-[#8FA89B] mt-1">{b.date} {b.start_time}{b.status === "live" ? " · LIVE" : ""}{b.watched ? " · watched" : ""}</p>
          </Link>
        ))}
      </div>
    </section>
  );
};

const TABS = [["all", "All"], ["live", "Live & Upcoming"], ["videos", "Videos"], ["articles", "Articles"]];
const LEGACY_TAB = { replays: "videos", podcasts: "videos" };

const Section = ({ k, title, children, more }) => (
  <section className="mb-14" data-testid={`media-section-${k}`}>
    <div className="flex items-end justify-between gap-4 mb-5">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1A242B]">{title}</h2>
      {more}
    </div>
    {children}
  </section>
);
const Cards = ({ xs }) => <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{xs.map((it) => <HubCard key={`${it.source}-${it.id}`} it={it} />)}</div>;
const More = ({ onClick, label, testid }) => (
  <button onClick={onClick} data-testid={testid} className="inline-flex items-center gap-1.5 text-sm font-medium text-[#144F75] hover:gap-2.5 transition-[gap] whitespace-nowrap">{label} <ArrowRight size={15} /></button>
);

export const MediaHub = () => {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab") || "all";
  const tab = LEGACY_TAB[raw] || raw;
  const tag = params.get("tag") || "";
  const [q, setQ] = useState("");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["media-hub", tag], queryFn: () => api.get(`/media-hub${tag ? `?tag=${encodeURIComponent(tag)}` : ""}`).then((r) => r.data), refetchInterval: 30000,
  });
  const { data: articles = [] } = useQuery({ queryKey: ["media-articles"], queryFn: () => api.get("/articles").then((r) => r.data) });
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const ql = q.toLowerCase();
  const match = (it) => !q || `${it.title} ${it.host || ""} ${(it.tags || []).join(" ")}`.toLowerCase().includes(ql);
  const live = (data?.live_now || []).filter(match);
  const coming = (data?.coming_up || []).filter(match);
  const sched = (data?.schedule || []).filter(match);
  const videos = (data?.videos || []).filter(match);
  const arts = articles.filter((a) => (!q || `${a.title} ${a.excerpt || ""} ${(a.tags || []).join(" ")}`.toLowerCase().includes(ql))
    && (!tag || (a.tags || []).map((t) => t.toLowerCase()).includes(tag.toLowerCase())));
  const [artLimit, setArtLimit] = useState(24);
  const nothingLive = !live.length && !coming.length && !sched.length;
  const all = tab === "all";
  const { user } = useAuth();

  return (
    <div data-testid="media-hub">
      {user && <MyBroadcasts />}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
        <div className="flex flex-wrap gap-2" role="tablist">
          {TABS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => set("tab", k === "all" ? "" : k)} data-testid={`media-tab-${k}`}
              className={`rounded-full px-4 py-2 text-sm transition-colors ${tab === k ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]"}`}>{l}</button>
          ))}
        </div>
        <label className="lg:ml-auto relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search media" aria-label="Search media" data-testid="media-search"
            className="rounded-full border border-[#E5E0D8] bg-white pl-9 pr-4 py-2 text-sm w-full lg:w-72 focus:outline-none focus:border-[#144F75]" />
        </label>
      </div>
      {tab !== "articles" && data?.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-8" data-testid="media-tags">
          <button onClick={() => set("tag", "")} data-testid="media-tag-all" className={`text-xs rounded-full px-3 py-1 ${!tag ? "bg-[#D98E73] text-white" : "bg-[#F5F1EA] text-[#4A5D6B]"}`}>All topics</button>
          {data.tags.map((t) => (
            <button key={t} onClick={() => set("tag", t)} data-testid={`media-tag-${t}`} className={`text-xs rounded-full px-3 py-1 ${tag === t ? "bg-[#D98E73] text-white" : "bg-[#F5F1EA] text-[#4A5D6B] hover:bg-[#EDE6DA]"}`}>#{t}</button>
          ))}
        </div>
      )}
      {isLoading && <div className="grid place-items-center py-20 text-[#4A5D6B]" data-testid="media-hub-loading"><Loader2 className="animate-spin" /></div>}
      {isError && <div className="text-center py-16" data-testid="media-hub-error"><p className="text-[#4A5D6B]">Media couldn't load.</p><button onClick={() => refetch()} className="eyw-btn !py-2 text-sm mt-3">Try again</button></div>}
      {!isLoading && !isError && (
        <>
          {(all || tab === "live") && (
            <>
              {live.length > 0 && <LiveViewer lives={live} />}
              {coming.length > 0 && <Section k="coming_up" title="Coming up"><Cards xs={all ? coming.slice(0, 3) : coming} /></Section>}
              {sched.length > 0 && (
                <Section k="schedule" title="Live schedule"
                  more={all && sched.length > 6 ? <More onClick={() => set("tab", "live")} label="Full schedule" testid="media-schedule-more" /> : null}>
                  <LiveSchedule items={sched} limit={all ? 6 : 0} />
                  {!all && <LiveAlertCTA compact />}
                </Section>
              )}
              {nothingLive && (tab === "live" || (!q && !tag)) && <LiveAlertCTA />}
            </>
          )}
          {(all || tab === "videos") && (videos.length > 0 ? (
            <Section k="videos" title="Videos" more={all && videos.length > 6 ? <More onClick={() => set("tab", "videos")} label="All videos" testid="media-videos-more" /> : null}>
              <Cards xs={all ? videos.slice(0, 6) : videos} />
            </Section>
          ) : tab === "videos" && <p className="text-center text-[#4A5D6B] py-16" data-testid="media-videos-empty">No videos match{tag ? ` #${tag}` : ""}{q ? ` "${q}"` : ""} yet — try another topic.</p>)}
          {(all || tab === "articles") && (arts.length > 0 ? (
            <Section k="articles" title="Articles & writings"
              more={all ? <More onClick={() => set("tab", "articles")} label="All articles" testid="media-all-articles" /> : <Link to="/articles" className="text-sm font-medium text-[#144F75]" data-testid="media-articles-library">Article library</Link>}>
              <ArticlesGrid articles={all ? arts.slice(0, 3) : arts.slice(0, artLimit)} />
              {!all && arts.length > artLimit && (
                <div className="text-center mt-8"><button onClick={() => setArtLimit(artLimit + 24)} className="eyw-btn-ghost !py-2 text-sm" data-testid="media-articles-load-more">Load more articles</button></div>
              )}
            </Section>
          ) : tab === "articles" && <p className="text-center text-[#4A5D6B] py-16" data-testid="media-articles-empty">No articles match{tag ? ` #${tag}` : ""}{q ? ` "${q}"` : ""} yet.</p>)}
          {all && (q || tag) && !live.length && !coming.length && !sched.length && !videos.length && !arts.length && (
            <p className="text-center text-[#4A5D6B] py-16" data-testid="media-all-empty">Nothing matches{tag ? ` #${tag}` : ""}{q ? ` "${q}"` : ""} — try another word or topic.</p>
          )}
        </>
      )}
    </div>
  );
};
